import React, { useState } from 'react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { postCourseProgramContent } from '../../../../models/course';

// Elementos que el modelo de IA extraerá del programa.
// Debe reflejar lo que realmente devuelve el backend (PROGRAM_CONTENT_SCHEMA:
// units[] con title / topics / summary / reference). El backend manda.
const EXTRACTION_ITEMS = [
  {
    title: 'Unidades y contenidos',
    description: 'Bloques temáticos del programa',
  },
  {
    title: 'Subtemas',
    description: 'Temas específicos que cubre cada unidad',
  },
  {
    title: 'Resumen por unidad',
    description: 'Descripción breve orientada a la búsqueda de recursos',
  },
  {
    title: 'Referencia en el programa',
    description: 'Dónde aparece cada unidad (sección o numeración)',
  },
];

// Panel de "Extraer con IA": describe lo que hará el modelo, lanza el
// procesamiento y muestra el contenido generado.
//
// Props:
// - courseId: id del curso a procesar.
// - programContent: contenido ya extraído (course.program_content) o null.
// - disabled: deshabilita el botón (ej. si no hay archivo cargado).
const ExtractAIPanel = ({ courseId, programContent, disabled = false }) => {
  const [content, setContent] = useState(programContent);
  const [loading, setLoading] = useState(false);

  const units = Array.isArray(content?.units) ? content.units : [];
  const hasContent = units.length > 0;

  const handleCreateProgramContent = async () => {
    setLoading(true);
    try {
      const { program_content } = await postCourseProgramContent(courseId);
      setContent(program_content);
    } catch (error) {
      // console.error(error);
      window.alert('Ocurrió un error al generar el contenido del programa.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='space-y-4'>
      <div className='rounded-xl border border-secondary/20 bg-secondary/5 p-4'>
        <p className='text-xs font-semibold uppercase tracking-wider text-secondary'>
          El modelo analizará y extraerá
        </p>
        <ul className='mt-3 space-y-3'>
          {EXTRACTION_ITEMS.map(item => (
            <li key={item.title} className='flex items-start gap-3'>
              <span className='mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-secondary' />
              <p className='text-sm text-foreground'>
                <span className='font-semibold text-primary'>{item.title}</span>
                <span className='text-muted-foreground'> — {item.description}</span>
              </p>
            </li>
          ))}
        </ul>
      </div>

      <button
        type='button'
        disabled={disabled || loading}
        onClick={handleCreateProgramContent}
        className='flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary to-secondary px-4 py-3 font-semibold text-white transition duration-150 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'>
        <AutoAwesomeIcon fontSize='small' />
        {loading
          ? 'Procesando...'
          : hasContent
          ? 'Volver a procesar con IA'
          : 'Procesar programa con IA'}
      </button>

      {hasContent && (
        <div className='rounded-xl border border-primary/10 bg-muted/30 p-4'>
          <div className='flex items-center justify-between'>
            <p className='text-xs font-semibold uppercase tracking-wider text-primary'>
              Contenido extraído
            </p>
            <span className='text-xs text-muted-foreground'>
              {units.length} {units.length === 1 ? 'unidad' : 'unidades'}
            </span>
          </div>

          <ol className='mt-3 space-y-3'>
            {units.map((unit, index) => (
              <li
                key={`${unit.title}-${index}`}
                className='rounded-lg border border-primary/10 bg-white p-3'>
                <div className='flex items-start justify-between gap-2'>
                  <p className='text-sm font-semibold text-foreground'>
                    {unit.title}
                  </p>
                  {unit.reference && (
                    <span className='flex-shrink-0 rounded-full bg-secondary/10 px-2 py-0.5 text-xs text-secondary'>
                      {unit.reference}
                    </span>
                  )}
                </div>

                {unit.summary && (
                  <p className='mt-1 text-xs text-muted-foreground'>
                    {unit.summary}
                  </p>
                )}

                {Array.isArray(unit.topics) && unit.topics.length > 0 && (
                  <div className='mt-2 flex flex-wrap gap-1.5'>
                    {unit.topics.map((topic, topicIndex) => (
                      <span
                        key={`${topic}-${topicIndex}`}
                        className='rounded-md bg-primary/5 px-2 py-0.5 text-xs text-primary'>
                        {topic}
                      </span>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

export default ExtractAIPanel;
